import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Stack,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  alpha,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';
import GppBadIcon from '@mui/icons-material/GppBad';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { updateUserFraudStatus } from '../actions/userAuth';

const PRESET_REASONS = [
  'Suspicious activity detected',
  'Repeated failed payments / orders (≥3 threshold)',
  'Potential payment abuse / chargeback risk',
  'Unauthorized account takeover attempt',
  'Identity verification mismatch',
];

export const FraudModal = ({ open, onClose, user, onSuccess }) => {
  const dispatch = useDispatch();
  const isCurrentlyFraud = Boolean(user?.isFraud);
  const [reason, setReason] = useState('Suspicious activity detected');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (isCurrentlyFraud) {
        setReason('Blacklist review cleared / user verified');
      } else {
        setReason('Suspicious activity detected');
      }
    }
  }, [open, isCurrentlyFraud]);

  const handleConfirm = () => {
    if (!user?.id) return;
    setLoading(true);

    const payload = {
      isFraud: !isCurrentlyFraud,
      reason: reason.trim() || (isCurrentlyFraud ? 'Removed from blacklist by admin' : 'Suspicious activity detected'),
    };

    dispatch(
      updateUserFraudStatus(
        user.id,
        payload,
        () => {
          setLoading(false);
          enqueueSnackbar(
            !isCurrentlyFraud
              ? 'User added to Blacklist! Account locked and active sessions revoked.'
              : 'User removed from Blacklist! Account access restored.',
            { variant: !isCurrentlyFraud ? 'warning' : 'success' }
          );
          if (typeof onSuccess === 'function') onSuccess();
          onClose();
        },
        (err) => {
          setLoading(false);
          const msg =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            'Failed to update blacklist status. Please try again.';
          enqueueSnackbar(msg, { variant: 'error' });
        }
      )
    );
  };

  if (!user) return null;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          p: { xs: 1, sm: 2 },
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.25)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, pr: 6, position: 'relative' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              bgcolor: isCurrentlyFraud ? alpha('#16a34a', 0.12) : alpha('#dc2626', 0.12),
              color: isCurrentlyFraud ? '#16a34a' : '#dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isCurrentlyFraud ? <LockOpenIcon /> : <GppBadIcon />}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
              {isCurrentlyFraud ? 'Remove User from Blacklist & Restore' : 'Blacklist User & Security Lockout'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              User: <strong style={{ color: '#0f172a' }}>{user.name || user.username || user.email}</strong> • ID:{' '}
              <span style={{ fontFamily: 'monospace' }}>{user.id}</span>
            </Typography>
          </Box>
        </Stack>

        <IconButton
          onClick={onClose}
          disabled={loading}
          sx={{ position: 'absolute', top: 16, right: 16 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {!isCurrentlyFraud ? (
          <Stack spacing={2.5}>
            {/* Warning banner */}
            <Alert
              severity="error"
              icon={<SecurityIcon fontSize="inherit" />}
              sx={{
                borderRadius: '14px',
                border: '1px solid rgba(220, 38, 38, 0.25)',
                '& .MuiAlert-message': { fontSize: '0.82rem', lineHeight: 1.5 },
              }}
            >
              <strong>Immediate Blacklist & Security Lockout Impact:</strong>
              <br />
              • Sets <code>isFraud = true</code>, <code>isBlocked = true</code>, and <code>isActive = false</code>
              <br />
              • Immediately invalidates and rejects all active JWT sessions
              <br />
              • Instantly stops all upcoming order checkout & transaction attempts
            </Alert>

            {/* Quick Reason Suggestions */}
            <Box>
              <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, display: 'block', mb: 1 }}>
                Quick Preset Reasons:
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {PRESET_REASONS.map((preset) => (
                  <Chip
                    key={preset}
                    label={preset}
                    size="small"
                    onClick={() => setReason(preset)}
                    clickable
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      borderRadius: '8px',
                      bgcolor: reason === preset ? alpha('#dc2626', 0.15) : alpha('#11409B', 0.05),
                      color: reason === preset ? '#dc2626' : '#334155',
                      border: `1px solid ${reason === preset ? alpha('#dc2626', 0.3) : alpha('#11409B', 0.1)}`,
                      '&:hover': { bgcolor: alpha('#dc2626', 0.1) },
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Reason textfield */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Blacklist Reason"
              placeholder="Enter reason or details regarding suspicious activity..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '14px',
                },
              }}
            />
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            {/* Current blacklist info banner */}
            <Box
              sx={{
                p: 2,
                borderRadius: '16px',
                bgcolor: alpha('#dc2626', 0.05),
                border: `1px solid ${alpha('#dc2626', 0.2)}`,
              }}
            >
              <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 700, textTransform: 'uppercase' }}>
                CURRENT BLACKLIST & FRAUD RECORD
              </Typography>
              <Typography sx={{ fontWeight: 600, color: '#0f172a', mt: 0.5, fontSize: '0.9rem' }}>
                Reason: "{user.fraudReason || 'No reason specified'}"
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Flagged by: <strong>{user.fraudFlaggedBy || 'SYSTEM'}</strong>
                </Typography>
                {user.fraudFlaggedAt && (
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Flagged at: {dayjs(user.fraudFlaggedAt).format('DD MMM YYYY, HH:mm')}
                  </Typography>
                )}
              </Stack>
            </Box>

            <Alert
              severity="info"
              sx={{
                borderRadius: '14px',
                fontSize: '0.82rem',
              }}
            >
              Restoring will set <code>isFraud = false</code> and remove the user from the blacklist so they can resume normal activity.
            </Alert>

            <TextField
              fullWidth
              label="Restoration Note / Reason"
              placeholder="Reason for restoring user (optional)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '14px',
                },
              }}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            color: '#64748b',
            borderColor: alpha('#64748b', 0.3),
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || (!isCurrentlyFraud && !reason.trim())}
          startIcon={
            loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : isCurrentlyFraud ? (
              <LockOpenIcon fontSize="small" />
            ) : (
              <LockPersonIcon fontSize="small" />
            )
          }
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            px: 2.5,
            py: 1,
            bgcolor: isCurrentlyFraud ? '#16a34a' : '#dc2626',
            '&:hover': {
              bgcolor: isCurrentlyFraud ? '#15803d' : '#b91c1c',
            },
          }}
        >
          {loading
            ? 'Updating...'
            : isCurrentlyFraud
            ? 'Remove from Blacklist & Restore'
            : 'Confirm Blacklist & Lockout'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FraudModal;
